import React, { useEffect, useMemo, useState } from 'react';
import {
  OverlayPopover,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { LeadExperience } from '../../../types';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';

interface ExperienceCellProps {
  experience?: LeadExperience[];
}

const ExperienceCell: React.FC<ExperienceCellProps> = ({ experience }) => {
  const [showPopover, setShowPopover] = useState(false);

  useEffect(() => {
    if (!showPopover) return () => {};

    const handleScrollCapture = (e: Event) => {
      const target = e.target as HTMLElement | null;
      // Don't close when user scrolls inside the popover itself.
      if (target?.closest?.('.bs-popover-inner, .popover')) {
        return;
      }
      setShowPopover(false);
    };

    document.addEventListener('scroll', handleScrollCapture, true);
    return () => {
      document.removeEventListener('scroll', handleScrollCapture, true);
    };
  }, [showPopover]);

  // Memoize experience formatting
  const formattedExperience = useMemo(
    () =>
      experience
        ?.map((exp) => {
          const title = exp?.job_title?.trim();
          const company = exp?.company_name?.trim();
          if (!title && !company) {
            return null;
          }
          if (title && company) {
            return `${title} @ ${company}`;
          }
          return title || company || null;
        })
        .filter(Boolean) || [],
    [experience],
  );

  if (formattedExperience.length === 0) {
    return <div className="v2-lead-email-cell">-</div>;
  }

  const firstExperience = formattedExperience[0];
  const remainingCount = formattedExperience.length - 1;

  return (
    <div className="v2-lead-email-cell">
      <TruncatedTextWithAutoTooltip
        className="v2-lead-email-text"
        text={firstExperience}
        placement={Placement.Auto}
      />
      {remainingCount > 0 && (
        <OverlayPopover
          show={showPopover}
          onToggle={setShowPopover}
          placement={Placement.Auto}
          className="v2-lead-emails-popover-container"
          content={
            <div className="v2-lead-emails-popover">
              {formattedExperience?.map((exp, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${exp}-${index}`}
                  className="v2-lead-email-item"
                >
                  <TruncatedTextWithAutoTooltip
                    className="v2-lead-email-text"
                    text={exp || ''}
                    placement={Placement.Auto}
                  />
                </div>
              ))}
            </div>
          }
          rootClose
        >
          <span
            className={`v2-lead-email-count ${showPopover ? 'active' : ''}`}
          >
            +{remainingCount}
          </span>
        </OverlayPopover>
      )}
    </div>
  );
};

// Memoize ExperienceCell to prevent unnecessary re-renders
export default React.memo(
  ExperienceCell,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps.experience) ===
    JSON.stringify(nextProps.experience),
);
